import { useState, useEffect } from 'react';

interface NestorConfig {
  activation_threshold: number;
  min_silence_duration: number;
  min_speech_duration: number;
  prefix_padding_duration: number;
  model: string;
  voice: string;
  temperature: number;
}

interface NestorStatus {
  connected: boolean;
  muted: boolean;
  target_participant: string | null;
}

interface ServiceStatus {
  pm2: Array<{
    name: string;
    status: string;
    cpu: number;
    memory: number;
    uptime: number;
    restarts: number;
  }>;
  nestor: {
    running: boolean;
    name: string;
  };
  fhvi: {
    running: boolean;
    name: string;
  };
  ports: Record<string, { active: boolean; name: string }>;
}

interface LiveKitRoom {
  name: string;
  numParticipants: number;
  createdAt: number;
}

interface LiveKitParticipant {
  identity: string;
  name: string;
  state: number;
  joinedAt: number;
  isPublishing: boolean;
  tracks: number;
  kind: number;
  role: string | null;
  targetLang: string | null;
}

const defaultConfig: NestorConfig = {
  activation_threshold: 0.6,
  min_silence_duration: 0.8,
  min_speech_duration: 0.1,
  prefix_padding_duration: 0.3,
  model: 'gpt-realtime-2.1',
  voice: 'coral',
  temperature: 0.7,
};

const voices = ['coral', 'shimmer', 'nova', 'marin', 'cedar', 'alloy', 'ash', 'ballad', 'echo', 'sage', 'verse'];

export default function MeetAdmin() {
  const [config, setConfig] = useState<NestorConfig>(defaultConfig);
  const [status, setStatus] = useState<NestorStatus>({
    connected: false,
    muted: false,
    target_participant: null,
  });
  const [services, setServices] = useState<ServiceStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [serviceAction, setServiceAction] = useState<string | null>(null);
  const [roomName, setRoomName] = useState('infinity-meet');
  const [rooms, setRooms] = useState<LiveKitRoom[]>([]);
  const [participants, setParticipants] = useState<LiveKitParticipant[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string>('infinity-meet');
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [kickingParticipant, setKickingParticipant] = useState<string | null>(null);

  // === V0.1 Auth admin ===
  const [authChecking, setAuthChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginBusy, setLoginBusy] = useState(false);

  // === V0.1 Translators ===
  const [translators, setTranslators] = useState<Array<{identity: string; targetLang: string; displayName: string; flag: string; state: string}>>([]);

  // V0.7 — Humains présents avec leur humanLang
  const [humans, setHumans] = useState<Array<{identity: string; name: string; humanLang: string | null}>>([]);
  const [humanLangBusy, setHumanLangBusy] = useState<string | null>(null);
  const [translatorPair, setTranslatorPair] = useState<'fr-sk' | 'fr-en' | 'en-sk' | 'fr-ja' | 'en-ja'>('fr-sk');
  // Pré-armement : le nom que l'admin portera dans la salle (le 1er traducteur s'y verrouille).
  const [prearmIdentity, setPrearmIdentity] = useState<string>('');
  const [prearmEtat, setPrearmEtat] = useState<string>('');
  const [translatorBusy, setTranslatorBusy] = useState(false);
  const [translatorMsg, setTranslatorMsg] = useState<string | null>(null);

  // Charger la config et le status au démarrage
  useEffect(() => {
    fetchConfig();
    fetchStatus();
    fetchServices();
    fetchRooms();
    const interval = setInterval(() => {
      fetchStatus();
      fetchServices();
      fetchRooms();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch('/api/nestor/config');
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Erreur chargement config:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/nestor/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const fetchServices = async () => {
    try {
      const [servicesRes, fhviRes] = await Promise.all([
        fetch('/api/services/status'),
        fetch('/api/services/fhvi/status'),
      ]);

      if (servicesRes.ok) {
        const data = await servicesRes.json();
        // Ajouter le status FHVI
        if (fhviRes.ok) {
          const fhviData = await fhviRes.json();
          data.fhvi = { running: fhviData.running, name: fhviData.name };
        }
        setServices(data);
      }
    } catch (err) {
      console.error('Erreur chargement services:', err);
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await fetch('/api/livekit/rooms');
      if (res.ok) {
        const data = await res.json();
        setRooms(data.rooms || []);
      }
    } catch (err) {
      // Silently fail
    }
  };

  // La console suit la room réellement active au lieu de rester figée sur
  // l'ancien nom historique `infinity-meet`.
  useEffect(() => {
    if (rooms.length === 0) return;
    if (!rooms.some((room) => room.name === selectedRoom)) {
      setSelectedRoom(rooms[0].name);
      setRoomName(rooms[0].name);
    }
  }, [rooms, selectedRoom]);

  const fetchParticipants = async (room: string) => {
    setLoadingParticipants(true);
    try {
      const res = await fetch(`/api/livekit/participants/${room}`);
      if (res.ok) {
        const data = await res.json();
        setParticipants(data.participants || []);
      }
    } catch (err) {
      console.error('Erreur chargement participants:', err);
      setParticipants([]);
    }
    setLoadingParticipants(false);
  };

  const kickParticipant = async (identity: string) => {
    if (!confirm(`Éjecter ${identity} de la salle ${selectedRoom} ?`)) return;
    setKickingParticipant(identity);
    try {
      const res = await fetch('/api/livekit/kick', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName: selectedRoom, participantIdentity: identity }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`✅ ${identity} éjecté`);
        fetchParticipants(selectedRoom);
      } else {
        setMessage(`❌ Erreur: ${data.message}`);
      }
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage('❌ Erreur lors de l\'éjection');
    }
    setKickingParticipant(null);
  };

  // Supprimer une salle (éjecte tous les participants)
  const deleteRoom = async (room: string) => {
    if (!confirm(`Supprimer la salle "${room}" ?\nTous les participants seront éjectés.`)) return;
    try {
      const res = await fetch(`/api/livekit/room/${room}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setMessage(`✅ Salle "${room}" supprimée`);
        fetchRooms();
        setParticipants([]);
      } else {
        setMessage(`❌ Erreur: ${data.message}`);
      }
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage('❌ Erreur lors de la suppression');
    }
  };

  // Charger les participants quand une salle est sélectionnée
  useEffect(() => {
    if (selectedRoom) {
      fetchParticipants(selectedRoom);
    }
  }, [selectedRoom]);

  const saveConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/nestor/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        setMessage('Configuration sauvegardée !');
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setMessage('Erreur de connexion');
    }
    setSaving(false);
  };

  const resetConfig = () => {
    setConfig(defaultConfig);
  };

  const updateConfig = (key: keyof NestorConfig, value: number | string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Actions sur Nestor
  const nestorAction = async (action: 'start' | 'stop' | 'restart') => {
    setServiceAction(`nestor-${action}`);
    try {
      const res = await fetch(`/api/services/nestor/${action}`, { method: 'POST' });
      const data = await res.json();
      setMessage(data.message);
      setTimeout(() => setMessage(null), 3000);
      // Rafraîchir le status
      setTimeout(fetchServices, 1500);
    } catch (err) {
      setMessage('Erreur lors de l\'action');
    }
    setServiceAction(null);
  };

  // Actions sur FHVI
  const fhviAction = async () => {
    setServiceAction('fhvi-restart');
    try {
      const res = await fetch('/api/services/fhvi/restart', { method: 'POST' });
      const data = await res.json();
      setMessage(data.message);
      setTimeout(() => setMessage(null), 3000);
      setTimeout(fetchServices, 2000);
    } catch (err) {
      setMessage('Erreur lors du redémarrage FHVI');
    }
    setServiceAction(null);
  };

  // Redémarrer tous les services
  const restartAllServices = async () => {
    if (!confirm('Redémarrer TOUS les services ? La page va se recharger.')) return;
    setServiceAction('restart-all');
    try {
      const res = await fetch('/api/services/restart-all', { method: 'POST' });
      const data = await res.json();
      setMessage(data.message);
      // Recharger la page après quelques secondes
      setTimeout(() => window.location.reload(), 5000);
    } catch (err) {
      setMessage('Erreur lors du redémarrage');
      setServiceAction(null);
    }
  };

  // Faire rejoindre Nestor à une salle
  const joinRoom = async () => {
    if (!roomName.trim()) {
      setMessage('Veuillez entrer un nom de salle');
      return;
    }
    setServiceAction('join-room');
    try {
      const res = await fetch('/api/services/nestor/join-room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName: roomName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(data.message);
      } else {
        setMessage(`Erreur: ${data.error}`);
      }
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      setMessage('Erreur lors de l\'envoi de Nestor');
    }
    setServiceAction(null);
  };

  const formatUptime = (ms: number) => {
    if (ms < 1000) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}j ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };


  // === V0.1 Auth gate + translators handlers ===
  useEffect(() => {
    fetch('/api/admin/me', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setAuthed(!!d.success); setAuthChecking(false); })
      .catch(() => { setAuthed(false); setAuthChecking(false); });
  }, []);

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError(null);
    try {
      const r = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({password: loginPassword}),
      });
      const d = await r.json();
      if (d.success) { setAuthed(true); setLoginPassword(''); }
      else setLoginError(d.error || 'Erreur');
    } catch (e: any) { setLoginError(e.message); }
    finally { setLoginBusy(false); }
  };

  const handleLogout = async () => {
    await fetch('/api/admin/logout', {method: 'POST', credentials: 'include'});
    setAuthed(false);
  };

  // PRÉ-ARMER : arme la paire AVANT que les gens arrivent, et le dit.
  const prearmTranslators = async () => {
    const [l1, l2] = pairToLanguages(translatorPair);
    const moi = prearmIdentity.trim();
    if (!moi) { setPrearmEtat('✗ Indiquez le nom que vous porterez dans la salle — le 1er traducteur s\'y verrouille.'); return; }
    setTranslatorBusy(true); setPrearmEtat('');
    try {
      const r = await fetch('/api/livekit/translators/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roomName: selectedRoom, pairs: [[l1, l2]], adminIdentity: moi }),
      });
      const d = await r.json();
      // ÉTAT VISIBLE, jamais un echec muet : on lit le resultat reel, pas le code HTTP seul.
      const ok = d.success && (d.results || []).some((x: any) => x.ok);
      if (ok) setPrearmEtat(`✓ Armé : ${l1.toUpperCase()} ↔ ${l2.toUpperCase()} — vous entrez en ${l1.toUpperCase()} sous « ${moi} », la 2e personne est prise en charge dès son arrivée.`);
      else setPrearmEtat(`✗ Échec : ${(d.results || []).map((x: any) => x.error).filter(Boolean).join(' · ') || d.error || 'raison inconnue'}`);
    } catch (e: any) { setPrearmEtat(`✗ Échec réseau : ${e?.message || 'inconnue'}`); }
    finally { setTranslatorBusy(false); }
  };

  const cancelPrearm = async () => {
    setTranslatorBusy(true);
    try {
      const r = await fetch('/api/livekit/translators/prearm/cancel', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ roomName: selectedRoom }),
      });
      const d = await r.json();
      setPrearmEtat(d.cancelled ? 'Pré-armement annulé.' : 'Aucun pré-armement en cours sur cette salle.');
    } catch (e: any) { setPrearmEtat(`✗ ${e?.message || 'echec'}`); }
    finally { setTranslatorBusy(false); }
  };

  // REPLI MUET SUPPRIME (14 aout 2026). Cette table listait trois paires et rendait ['fr','sk']
  // pour TOUT LE RESTE : en choisissant FR↔JA, le menu affichait bien le japonais, le bouton se
  // relibellait, et l'armement partait en SLOVAQUE. Un defaut qui ne se voit qu'a l'usage, sur un
  // appel reel. La paire se DERIVE maintenant de sa propre valeur — plus de seconde source de
  // verite a tenir a jour a chaque langue ajoutee — et une valeur inconnue CRIE au lieu de se
  // rabattre sur une paire plausible.
  const LANGUES_CONNUES = ['fr', 'en', 'sk', 'rw', 'ja'];
  const pairToLanguages = (pair: string): string[] => {
    const parties = String(pair).split('-');
    if (parties.length !== 2 || !parties.every((l) => LANGUES_CONNUES.includes(l))) {
      throw new Error(`paire inconnue : ${pair}`);
    }
    return parties;
  };

  const fetchHumans = async (room: string) => {
    try {
      const r = await fetch(`/api/livekit/participants/${encodeURIComponent(room)}`, {credentials: 'include'});
      const d = await r.json();
      if (d.success) {
        const onlyHumans = (d.participants || []).filter((p: any) => Number(p.kind || 0) !== 4 && p.role !== 'translator');
        setHumans(onlyHumans.map((p: any) => ({identity: p.identity, name: p.name || p.identity, humanLang: p.humanLang})));
      }
    } catch (e) { /* silent */ }
  };

  const setHumanLangFor = async (identity: string, humanLang: string | null) => {
    setHumanLangBusy(identity);
    try {
      const r = await fetch('/api/livekit/translators/set-human-lang', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({roomName: selectedRoom, identity, humanLang}),
      });
      const d = await r.json();
      if (d.success) {
        fetchHumans(selectedRoom);
      }
    } catch (e) { /* silent */ }
    finally { setHumanLangBusy(null); }
  };

  const fetchTranslatorStatus = async (room: string) => {
    try {
      const r = await fetch(`/api/livekit/translators/status?roomName=${encodeURIComponent(room)}`, {credentials: 'include'});
      const d = await r.json();
      if (d.success) setTranslators(d.translators || []);
    } catch (e) { /* silent */ }
  };

  const startTranslators = async () => {
    setTranslatorBusy(true);
    setTranslatorMsg(null);
    try {
      const r = await fetch('/api/livekit/translators/start', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({roomName: selectedRoom, languages: pairToLanguages(translatorPair)}),
      });
      const d = await r.json();
      if (d.success) {
        const okCount = d.results.filter((x: any) => x.ok).length;
        setTranslatorMsg(`✓ ${okCount}/${d.results.length} translators dispatchés dans ${selectedRoom}`);
        setTimeout(() => fetchTranslatorStatus(selectedRoom), 2000);
      } else setTranslatorMsg(`Erreur : ${d.error}`);
    } catch (e: any) { setTranslatorMsg(`Erreur : ${e.message}`); }
    finally { setTranslatorBusy(false); }
  };

  const stopTranslators = async () => {
    setTranslatorBusy(true);
    setTranslatorMsg(null);
    try {
      const r = await fetch('/api/livekit/translators/stop', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({roomName: selectedRoom}),
      });
      const d = await r.json();
      if (d.success) { setTranslatorMsg(`✓ ${d.count} translator(s) kické(s)`); setTranslators([]); }
      else setTranslatorMsg(`Erreur : ${d.error}`);
    } catch (e: any) { setTranslatorMsg(`Erreur : ${e.message}`); }
    finally { setTranslatorBusy(false); }
  };

  useEffect(() => {
    if (!authed || !selectedRoom) return;
    fetchTranslatorStatus(selectedRoom);
    fetchHumans(selectedRoom);
    fetchParticipants(selectedRoom);
    const id = setInterval(() => {
      fetchTranslatorStatus(selectedRoom);
      fetchHumans(selectedRoom);
      fetchParticipants(selectedRoom);
    }, 5000);
    return () => clearInterval(id);
  }, [authed, selectedRoom]);

  const nestorInRoom = participants.some((participant) =>
    participant.kind === 4 && participant.role !== 'translator'
  );
  const roomMode = translators.length > 0
    ? (nestorInRoom ? 'CONFLIT' : 'TRADUCTION')
    : (nestorInRoom ? 'AGENT' : 'AUCUN');

  return (
    <>
      {authChecking && (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center text-white">
          <div className="text-lg">Chargement…</div>
        </div>
      )}
      {!authChecking && !authed && (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
          <form onSubmit={submitLogin} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 w-full max-w-sm">
            <h2 className="text-xl font-semibold text-white mb-6 text-center">🔒 Accès Admin</h2>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 mb-4"
            />
            {loginError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/20 text-red-300 text-sm">{loginError}</div>
            )}
            <button
              type="submit"
              disabled={loginBusy || !loginPassword}
              className="w-full px-4 py-3 rounded-lg bg-purple-500/50 hover:bg-purple-500/70 disabled:opacity-40 disabled:cursor-not-allowed transition text-white font-medium"
            >
              {loginBusy ? '…' : 'Connexion'}
            </button>
          </form>
        </div>
      )}
      {!authChecking && authed && (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Nestor Admin</h1>
            <p className="text-gray-400 mt-1">Configuration de l'agent vocal & Services</p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/meet"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition"
            >
              Retour à Meet
            </a>
          </div>
        </div>

        {/* Message global */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('Erreur') ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
            {message}
          </div>
        )}

        {/* Restart All Services */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-orange-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🔄</span>
              <div>
                <h2 className="text-lg font-semibold">Redémarrer tous les services</h2>
                <p className="text-sm text-gray-400">RESTART_ALL_SERVICES.command</p>
              </div>
            </div>
            <button
              onClick={restartAllServices}
              disabled={serviceAction !== null}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center gap-2"
            >
              {serviceAction === 'restart-all' ? (
                <>⏳ Redémarrage en cours...</>
              ) : (
                <>🚀 Tout redémarrer</>
              )}
            </button>
          </div>
        </div>

        <div className={`rounded-2xl p-5 mb-6 border ${roomMode === 'CONFLIT' ? 'bg-red-500/20 border-red-400/50' : roomMode === 'TRADUCTION' ? 'bg-cyan-500/15 border-cyan-400/40' : roomMode === 'AGENT' ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-white/5 border-white/10'}`}>
          <div className="text-xs uppercase tracking-widest text-gray-400">Mode de la salle {selectedRoom}</div>
          <div className="text-2xl font-semibold mt-1">{roomMode}</div>
          <div className="text-sm text-gray-300 mt-1">
            {roomMode === 'AGENT' && 'Nestor est actif. Démarrer la traduction le retirera automatiquement.'}
            {roomMode === 'TRADUCTION' && 'Les traducteurs sont actifs. Envoyer Nestor les arrêtera automatiquement.'}
            {roomMode === 'AUCUN' && 'Ni Nestor ni traducteur ne sont actifs dans cette salle.'}
            {roomMode === 'CONFLIT' && 'État anormal : agent et traducteurs coexistent. Changez de mode pour nettoyer la salle.'}
          </div>
        </div>

        {/* LiveKit Rooms & Participants */}
        <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-blue-500/20">
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
            <span className="text-2xl">🎥</span> LiveKit - Salles & Participants
          </h2>

          {/* Salles actives */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-3">Salles actives ({rooms.length})</h3>
            {rooms.length === 0 ? (
              <div className="text-gray-500 text-sm p-4 bg-white/5 rounded-lg">Aucune salle active</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {rooms.map((room) => (
                  <div key={room.name} className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedRoom(room.name)}
                      className={`px-4 py-2 rounded-l-lg transition flex items-center gap-2 ${
                        selectedRoom === room.name
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <span>{room.name}</span>
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                        {room.numParticipants}
                      </span>
                    </button>
                    <button
                      onClick={() => deleteRoom(room.name)}
                      className="px-3 py-2 rounded-r-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 transition"
                      title="Supprimer la salle"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Participants de la salle sélectionnée */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <span>👥</span> Participants dans "{selectedRoom}"
                {loadingParticipants && <span className="text-xs text-gray-400">(chargement...)</span>}
              </h3>
              <button
                onClick={() => fetchParticipants(selectedRoom)}
                className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition"
              >
                🔄 Rafraîchir
              </button>
            </div>

            {participants.length === 0 ? (
              <div className="text-gray-500 text-sm">Aucun participant dans cette salle</div>
            ) : (
              <div className="space-y-2">
                {participants.map((p) => (
                  <div
                    key={p.identity}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${p.isPublishing ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
                      <div>
                        <div className="font-medium">{p.name || p.identity}</div>
                        <div className="text-xs text-gray-400">
                          {p.identity} • {p.tracks} tracks • {p.isPublishing ? '📹 Publishing' : '👁️ Spectateur'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => kickParticipant(p.identity)}
                      disabled={kickingParticipant === p.identity}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded-lg text-sm font-medium transition disabled:opacity-50"
                    >
                      {kickingParticipant === p.identity ? '⏳...' : '🚫 Kick'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Services Panel */}
        {/* ═════ V0.7 Humains présents ═════ */}
        <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-blue-500/20">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            👥 Humains présents
            <span className="text-xs text-blue-300 font-normal">— Room : {selectedRoom}</span>
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Set la langue de chaque humain (ou laisse-le cliquer son drapeau dans /meet). Le dispatch des translators a besoin que chaque humain ait une langue assignée.
          </p>
          {humans.length === 0 ? (
            <div className="text-xs text-gray-500 italic">Aucun humain dans cette room — joignez /meet d'abord</div>
          ) : (
            <div className="space-y-2">
              {humans.map((h) => (
                <div key={h.identity} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{h.humanLang === 'fr' ? '🇫🇷' : h.humanLang === 'en' ? '🇬🇧' : h.humanLang === 'sk' ? '🇸🇰' : '❓'}</div>
                    <div>
                      <div className="text-sm font-medium text-white">{h.name}</div>
                      <div className="text-xs text-gray-400">{h.humanLang ? `humanLang=${h.humanLang}` : 'aucune langue assignée'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {['fr', 'en', 'sk'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setHumanLangFor(h.identity, lang)}
                        disabled={humanLangBusy === h.identity || h.humanLang === lang}
                        className={`px-3 py-1.5 rounded text-sm transition ${h.humanLang === lang ? 'bg-blue-500/40 border border-blue-400/60' : 'bg-white/10 hover:bg-white/20 border border-transparent'} disabled:opacity-50`}
                      >
                        {lang === 'fr' ? '🇫🇷' : lang === 'en' ? '🇬🇧' : '🇸🇰'}
                      </button>
                    ))}
                    {h.humanLang && (
                      <button
                        onClick={() => setHumanLangFor(h.identity, null)}
                        disabled={humanLangBusy === h.identity}
                        title="Réinitialiser"
                        className="px-2 py-1.5 rounded text-xs bg-red-500/20 hover:bg-red-500/40 transition"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {humans.length > 0 && humans.some(h => !h.humanLang) && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
              ⚠ Certains humains n'ont pas de langue. Le dispatch ne traitera que ceux qui en ont une.
            </div>
          )}
        </div>

        {/* ═════ V0.1 Section Translateurs ═════ */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-emerald-500/20">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            🌐 Traducteurs realtime
            <span className="text-xs text-emerald-300 font-normal">— Room : {selectedRoom}</span>
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Mode A par défaut côté /meet : voix originale du locuteur étrangère muté, seule la voix traduite est audible.
            Toggle "Mode B" disponible dans /meet pour entendre les deux simultanément.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <label className="text-sm text-gray-300">Paire de langues :</label>
            <select
              value={translatorPair}
              onChange={(e) => setTranslatorPair(e.target.value as any)}
              disabled={translatorBusy || translators.length > 0}
              className="bg-white/10 border border-white/20 rounded px-3 py-2 text-sm"
            >
              <option value="fr-sk">FR ↔ SK</option>
              <option value="fr-en">FR ↔ EN</option>
              <option value="en-sk">EN ↔ SK</option>
              <option value="fr-ja">FR ↔ JA</option>
              <option value="en-ja">EN ↔ JA</option>
            </select>
            <input
              type="text"
              value={prearmIdentity}
              onChange={(e) => setPrearmIdentity(e.target.value)}
              placeholder="Votre nom dans la salle"
              className="bg-white/10 border border-white/20 rounded px-3 py-2 text-sm w-52"
            />
            <button onClick={prearmTranslators} disabled={translatorBusy || !selectedRoom}
              className="px-4 py-2 rounded-lg bg-amber-500/30 hover:bg-amber-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              {translatorBusy ? '...' : 'Pré-armer (salle vide)'}
            </button>
            <button onClick={cancelPrearm} disabled={translatorBusy || !selectedRoom}
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm">
              Annuler l'armement
            </button>
            <button onClick={startTranslators} disabled={translatorBusy || translators.length > 0 || !selectedRoom}
              className="px-4 py-2 rounded-lg bg-emerald-500/30 hover:bg-emerald-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              {translatorBusy ? '...' : `Lancer ${translatorPair.toUpperCase()}`}
            </button>
            <button onClick={stopTranslators} disabled={translatorBusy || translators.length === 0}
              className="px-4 py-2 rounded-lg bg-red-500/30 hover:bg-red-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              Stop translators
            </button>
            <button onClick={() => fetchTranslatorStatus(selectedRoom)} disabled={!selectedRoom}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm">
              ↻ Status
            </button>
            {prearmEtat && (
              <div className="w-full text-sm mt-1" style={{ color: prearmEtat.startsWith('✓') ? '#6ee7b7' : '#fca5a5' }}>
                {prearmEtat}
              </div>
            )}
          </div>
          {translatorMsg && (
            <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10 text-sm">{translatorMsg}</div>
          )}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-sm text-gray-400 mb-2">Translators actifs : <span className="font-medium text-white">{translators.length}</span></div>
            {translators.length === 0 ? (
              <div className="text-xs text-gray-500">Aucun translator dans cette room</div>
            ) : (
              <div className="space-y-2">
                {translators.map((t) => (
                  <div key={t.identity} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                    <div className="text-3xl leading-none">{t.flag}</div>
                    <div className="text-sm font-medium text-white">{t.displayName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
            <span className="text-2xl">⚡</span> Services
          </h2>

          {/* Nestor Control */}
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🤖</span>
                <div>
                  <h3 className="font-semibold text-lg">Nestor - Agent Vocal</h3>
                  <p className="text-sm text-gray-400">OpenAI Realtime + LiveKit</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                services?.nestor?.running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <div className={`w-3 h-3 rounded-full ${services?.nestor?.running ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                {services?.nestor?.running ? 'En cours' : 'Arrêté'}
              </div>
            </div>

            <div className="flex gap-3 mb-4">
              <button
                onClick={() => nestorAction('start')}
                disabled={serviceAction !== null || services?.nestor?.running}
                className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                <span>▶️</span> {serviceAction === 'nestor-start' ? 'Démarrage...' : 'Démarrer'}
              </button>
              <button
                onClick={() => nestorAction('stop')}
                disabled={serviceAction !== null || !services?.nestor?.running}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                <span>⏹️</span> {serviceAction === 'nestor-stop' ? 'Arrêt...' : 'Arrêter'}
              </button>
              <button
                onClick={() => nestorAction('restart')}
                disabled={serviceAction !== null}
                className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                <span>🔄</span> {serviceAction === 'nestor-restart' ? 'Redémarrage...' : 'Redémarrer'}
              </button>
            </div>

            {/* Envoyer Nestor dans une salle */}
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <label className="block text-sm text-gray-400 mb-2">Envoyer Nestor dans une salle</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="Nom de la salle (ex: infinity-meet)"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
                <button
                  onClick={joinRoom}
                  disabled={serviceAction !== null || !services?.nestor?.running}
                  className="px-6 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center gap-2"
                >
                  <span>🚀</span> {serviceAction === 'join-room' ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Nestor doit être démarré. Il rejoindra la salle spécifiée.</p>
            </div>
          </div>

          {/* FHVI Control */}
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">📊</span>
                <div>
                  <h3 className="font-semibold text-lg">FHVI Dashboard</h3>
                  <p className="text-sm text-gray-400">Portail client - Port 3006</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                services?.fhvi?.running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <div className={`w-3 h-3 rounded-full ${services?.fhvi?.running ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                {services?.fhvi?.running ? 'En cours' : 'Arrêté'}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={fhviAction}
                disabled={serviceAction !== null}
                className="flex-1 px-4 py-3 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                <span>🔄</span> {serviceAction === 'fhvi-restart' ? 'Redémarrage...' : 'Redémarrer FHVI'}
              </button>
              <a
                href="/portail-client"
                target="_blank"
                className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                <span>🔗</span> Ouvrir le portail
              </a>
            </div>
          </div>

          {/* Ports Status */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-3">Ports actifs</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {services?.ports && Object.entries(services.ports).map(([port, info]) => (
                <div
                  key={port}
                  className={`p-3 rounded-lg ${info.active ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5 border border-white/10'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${info.active ? 'bg-green-400' : 'bg-gray-500'}`} />
                    <span className="font-mono text-sm">:{port}</span>
                  </div>
                  <div className="text-xs text-gray-400">{info.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* PM2 Services */}
          {services?.pm2 && services.pm2.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Services PM2</h3>
              <div className="space-y-2">
                {services.pm2.map((service) => (
                  <div
                    key={service.name}
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      service.status === 'online' ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${service.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`} />
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>CPU: {service.cpu}%</span>
                      <span>RAM: {service.memory}MB</span>
                      <span>Uptime: {formatUptime(service.uptime)}</span>
                      <span>Restarts: {service.restarts}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Nestor Status */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-lg font-semibold mb-4">État de Nestor</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-4 ${services?.nestor?.running ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              <div className="text-gray-400 text-sm">Status</div>
              <div className="font-medium">{services?.nestor?.running ? 'Connecté' : 'Déconnecté'}</div>
            </div>
            <div className={`rounded-lg p-4 ${status.muted ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
              <div className="text-gray-400 text-sm">Mode</div>
              <div className="font-medium">{status.muted ? '🔇 Muté' : '🎙️ Actif'}</div>
            </div>
            <div className="rounded-lg p-4 bg-white/5">
              <div className="text-gray-400 text-sm">Cible</div>
              <div className="font-medium">{status.target_participant || 'Tout le monde'}</div>
            </div>
          </div>
        </div>

        {/* VAD Configuration */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-lg font-semibold mb-6">Détection Vocale (VAD)</h2>

          {/* Activation Threshold */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <label className="text-gray-300">Seuil d'activation</label>
              <span className="text-purple-400 font-mono">{config.activation_threshold.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.3"
              max="0.9"
              step="0.05"
              value={config.activation_threshold}
              onChange={(e) => updateConfig('activation_threshold', parseFloat(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Très sensible (0.3)</span>
              <span>Peu sensible (0.9)</span>
            </div>
          </div>

          {/* Min Silence Duration */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <label className="text-gray-300">Durée silence min (fin de parole)</label>
              <span className="text-purple-400 font-mono">{config.min_silence_duration.toFixed(2)}s</span>
            </div>
            <input
              type="range"
              min="0.3"
              max="2.0"
              step="0.1"
              value={config.min_silence_duration}
              onChange={(e) => updateConfig('min_silence_duration', parseFloat(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Rapide (0.3s)</span>
              <span>Patient (2.0s)</span>
            </div>
          </div>

          {/* Min Speech Duration */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <label className="text-gray-300">Durée parole min (ignorer bruits courts)</label>
              <span className="text-purple-400 font-mono">{config.min_speech_duration.toFixed(2)}s</span>
            </div>
            <input
              type="range"
              min="0.02"
              max="0.5"
              step="0.02"
              value={config.min_speech_duration}
              onChange={(e) => updateConfig('min_speech_duration', parseFloat(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Réactif (0.02s)</span>
              <span>Filtré (0.5s)</span>
            </div>
          </div>

          {/* Prefix Padding */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <label className="text-gray-300">Padding audio (avant parole détectée)</label>
              <span className="text-purple-400 font-mono">{config.prefix_padding_duration.toFixed(2)}s</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={config.prefix_padding_duration}
              onChange={(e) => updateConfig('prefix_padding_duration', parseFloat(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Court (0.1s)</span>
              <span>Long (1.0s)</span>
            </div>
          </div>
        </div>

        {/* Voice Configuration */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
          <h2 className="text-lg font-semibold mb-6">Voix OpenAI</h2>

          {/* Voice Selection */}
          <div className="mb-6">
            <label className="text-gray-300 block mb-3">Voix</label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {voices.map((v) => (
                <button
                  key={v}
                  onClick={() => updateConfig('voice', v)}
                  className={`px-4 py-2 rounded-lg capitalize transition ${
                    config.voice === v
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Temperature */}
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-gray-300">Température (créativité)</label>
              <span className="text-purple-400 font-mono">{config.temperature.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={config.temperature}
              onChange={(e) => updateConfig('temperature', parseFloat(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Précis (0.1)</span>
              <span>Créatif (1.0)</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={resetConfig}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition"
          >
            Réinitialiser
          </button>

          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-8 py-3 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 rounded-lg font-semibold transition"
          >
            {saving ? 'Sauvegarde...' : 'Appliquer la config'}
          </button>
        </div>

        {/* Help */}
        <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
          <h3 className="font-semibold mb-2">Conseils</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• <strong>Feedback audio ?</strong> Augmentez le seuil d'activation ou utilisez un casque</li>
            <li>• <strong>Nestor coupe trop tôt ?</strong> Augmentez la durée de silence minimum</li>
            <li>• <strong>Bruits parasites ?</strong> Augmentez la durée de parole minimum</li>
            <li>• <strong>Début coupé ?</strong> Augmentez le padding audio</li>
            <li>• <strong>Après changement de config :</strong> Redémarrez Nestor pour appliquer</li>
          </ul>
        </div>
      </div>
    </div>
      )}
    </>
  );
}
